import { useEffect, useState } from 'react'
import { conteosStockApi } from '../api/conteosStock'
import Boton from '../components/Boton'
import { colors, text, space, sx } from '../design/tokens'
import { fmtHoraDe } from '../utils/fecha'
import { useAutoRefresh } from '../hooks/useAutoRefresh'

// STOCK → Inventario (Martin, 29 oct 2026 — docs/diseno-inventario-ciclico.md):
// los conteos del día hechos desde el celular (/m/conteo), con el teórico de
// Presea a la hora de cada conteo y la diferencia (el ajuste propuesto).
// Fase 1: solo lectura; la aprobación y el archivo del ajuste vienen después.
const ESTADOS = {
  pendiente: { t: 'Calculando', bg: colors.bgSoft, c: colors.textSec },
  calculado: { t: 'Calculado', bg: colors.okBg, c: colors.ok },
  reconteo: { t: 'Revisar', bg: colors.warnBg, c: colors.warn },
  descartado: { t: 'Reemplazado', bg: colors.bgSoft, c: colors.textMeta },
}

// Un conteo pendiente que ya falló no se está calculando: se dice que no se pudo.
const SIN_CALCULAR = { t: 'Sin calcular', bg: colors.errBg, c: colors.err }
const estadoDe = (c) => (c.estado === 'pendiente' && c.error ? SIN_CALCULAR : (ESTADOS[c.estado] || ESTADOS.pendiente))

// Fila densa del sistema: 44 px mínimo, divisor suave, padding vertical para
// las sub-líneas (SKU, marcas, error).
const td = { ...sx.td, padding: `${space.md}px ${space.xl}px`, height: 44, borderBottom: `1px solid ${colors.borderSoft}` }
const tdNum = { ...td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }
const fmtNum = (n) => (n == null ? '—' : Number(n).toLocaleString('es-AR', { maximumFractionDigits: 2 }))

const hoy = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' })
const fmtCant = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${Number(n).toLocaleString('es-AR', { maximumFractionDigits: 2 })}`)

export default function InventarioPage() {
  const [fecha, setFecha] = useState(hoy())
  const [conteos, setConteos] = useState(null)
  const [error, setError] = useState(null)
  const [recalculando, setRecalculando] = useState(null)

  const cargar = () => conteosStockApi.listar({ fecha })
    .then(({ data }) => { setConteos(data.conteos); setError(null) })
    .catch(() => setError('No se pudieron cargar los conteos.'))

  useEffect(() => { cargar() }, [fecha]) // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(cargar, 30000)

  const recalcular = (id) => {
    setRecalculando(id)
    return conteosStockApi.recalcular(id).then(cargar).catch(() => setError('No se pudo recalcular.')).finally(() => setRecalculando(null))
  }
  const esHoy = fecha === hoy()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.xl }}>
      <div>
        <h1 style={{ margin: 0 }}>Inventario</h1>
        <p style={{ margin: '4px 0 0', fontSize: text.sec, color: colors.textSec }}>
          Se cuenta desde el celular (LGR móvil → Conteo). Teórico = lo que decía Presea a la hora del conteo; ajuste = contado − teórico.
        </p>
      </div>

      <div style={{ display: 'flex', gap: space.sm, alignItems: 'center' }}>
        <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={sx.input} />
        <button onClick={() => setFecha(hoy())} style={{ ...sx.pill, ...(esHoy ? sx.pillActive : {}) }}>Hoy</button>
      </div>

      {error && <div style={{ color: colors.err, fontSize: text.body }}>{error}</div>}
      {!conteos && !error && <div style={{ color: colors.textMeta, fontSize: text.body }}>Cargando…</div>}
      {conteos && conteos.length === 0 && <div style={{ ...sx.card, color: colors.textMeta, fontSize: text.body }}>No hay conteos en esta fecha.</div>}
      {conteos && conteos.length > 0 && (
        <div style={{ ...sx.card, padding: 0, overflowX: 'auto' }}>
          <table style={sx.table}>
            <thead>
              <tr>{[['Hora'], ['Producto'], ['Depósito'], ['Contó'], ['Contado', 1], ['Teórico', 1], ['Ajuste', 1], ['Estado'], ['Acción']].map(([h, num]) => <th key={h} style={{ ...sx.th, ...(num ? { textAlign: 'right' } : {}) }}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {conteos.map(c => {
                const e = estadoDe(c)
                const descartado = c.estado === 'descartado'
                return (
                  <tr key={c.id} style={{ opacity: descartado ? 0.55 : 1 }}>
                    <td style={{ ...td, ...sx.mono, whiteSpace: 'nowrap' }}>{fmtHoraDe(c.contado_at)}</td>
                    <td style={td}>
                      <div>{c.descripcion}</div>
                      <div style={{ fontSize: text.sec, color: colors.textSec }}><span style={sx.mono}>{c.sku}</span></div>
                      {(c.marcas || []).map((m, i) => <div key={i} style={{ fontSize: text.sec, color: colors.warn }}>⚠ {m.texto}</div>)}
                      {c.error && <div style={{ fontSize: text.sec, color: colors.err }}>{c.error}</div>}
                    </td>
                    <td style={{ ...td, ...sx.mono }}>{c.deposito}</td>
                    <td style={td}>{c.usuario || '—'}</td>
                    <td style={tdNum}>{fmtNum(c.cantidad)}</td>
                    <td style={tdNum}>{fmtNum(c.teorico)}</td>
                    <td style={{ ...tdNum, fontWeight: 600 }}>{fmtCant(c.diferencia)}</td>
                    <td style={td}><span style={{ ...sx.badge, background: e.bg, color: e.c, whiteSpace: 'nowrap' }}>{e.t}</span></td>
                    <td style={td}>
                      {descartado
                        ? <span style={{ color: colors.textMeta }}>—</span>
                        : <Boton variante="secondary" sm disabled={recalculando === c.id} onClick={() => recalcular(c.id)}>{recalculando === c.id ? 'Recalculando…' : '↻ Recalcular'}</Boton>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
